import {
  useGetLeaderboard,
  useInvalidateGetRewards,
} from '../../providers/queries/rbe';
import { FC, ReactNode, useMemo, useEffect } from 'react';
import { EventData, EventTypeEnum } from '@wwe-portal/entities/rbe.type';
import { correctLeaderboardOrder } from '@wwe-portal/web/hooks/rbe/use-limited-events';
import styles from './solo-event-leaderboard.module.css';
import Image from 'next/image';
import { cn } from '@wwe-portal/ui/lib/lib';
import { formatDateTime } from '@wwe-portal/utils/date';
import { InfoTooltip } from '@wwe-portal/ui/components/tooltip/info-tooltip';
import { Button } from '@wwe-portal/ui/components/button';
import { showDialog } from '@wwe-portal/ui/components/dialog';
import { PublicUsernameCheckbox } from './public-username-checkbox';

import { LeaderboardRewardsDialog } from './leaderboard-rewards-dialog';
import { useIsAuthenticated } from '@wwe-portal/web/providers/stores/auth-store';
import { SoloEventLeaderboardEmpty } from './solo-event-leaderboard-empty';
import {
  SoloEventLeaderboardRow,
  SoloEventLeaderboardRowLayout,
  SoloEventLeaderboardRowSkeleton,
} from './solo-event-leaderboard-row';
import { LoginDialogPortable } from '../auth/login-dialog';
import { YourCurrentRewardsBanner } from './your-current-rewards-banner';
import { ClientOnly } from '../common/client-only';
import { EventEndedBanner } from './event-ended-banner';
import { useTimeLeft } from '@wwe-portal/ui/lib/hooks';

const SoloEventLeaderboardLayout: FC<{
  banner: ReactNode;
  header: ReactNode;
  body: ReactNode;
}> = ({ banner, header, body }) => {
  return (
    <div>
      {banner}
      <div className="bg-back600 rounded-xl">
        {header}
        <div className="text-text400 bg-back300 px-5 pb-4 pt-8 text-xs">
          {body}
        </div>
      </div>
    </div>
  );
};

const SoloEventLeaderboardSkeleton: FC<{ header?: ReactNode }> = ({
  header,
}) => {
  return (
    <SoloEventLeaderboardLayout
      banner={null}
      header={header}
      body={
        <>
          {new Array(10).fill(0).map((_, index) => (
            <SoloEventLeaderboardRowSkeleton key={index} />
          ))}
        </>
      }
    />
  );
};

export const SoloEventLeaderboard: FC<{
  event: EventData;
  rowsLimit?: number;
}> = ({ event, rowsLimit = 10 }) => {
  const isAuthenticated = useIsAuthenticated();
  const { leaderboard: rawData, isLoading } = useGetLeaderboard(
    {
      portalEventId: String(event.portalEventId),
      typeId: event.typeId,
    },
    {
      enabled:
        event.typeId === EventTypeEnum.SOLO || event.leaderboardId != null,
    }
  );
  const leaderboard = correctLeaderboardOrder(rawData);

  // Invalidate reward status when event ends to trigger auto-claim modal
  const invalidateGetRewards = useInvalidateGetRewards();
  const { ended } = useTimeLeft(event.endDate);

  useEffect(() => {
    if (ended) {
      invalidateGetRewards();
    }
  }, [ended, invalidateGetRewards]);

  const top10 = useMemo(() => {
    return leaderboard?.rows?.slice(0, rowsLimit) ?? [];
  }, [leaderboard, rowsLimit]);
  const currentPlayerRow = leaderboard?.currentPlayer;
  const abovePlayer = leaderboard?.abovePlayer;
  const isShowPlayerWithSiblings =
    isAuthenticated &&
    currentPlayerRow?.userId &&
    !top10.some((player) => player?.userId === currentPlayerRow?.userId);

  const abovePlayerRank = Number(leaderboard?.abovePlayer?.playerRank);
  const currentPlayerRowRank = Number(leaderboard?.currentPlayer?.playerRank);
  const isShowAbovePlayer = Boolean(
    abovePlayer &&
      currentPlayerRow &&
      abovePlayer?.userId &&
      abovePlayer.playerPoints >= currentPlayerRow.playerPoints &&
      !top10.some((player) => player?.userId === abovePlayer?.userId)
  );

  const handleShowRewardsDialog = () => {
    if (!isAuthenticated) {
      showDialog(LoginDialogPortable);
      return;
    }
    showDialog(LeaderboardRewardsDialog, {
      brackets: leaderboard?.brackets,
      currentPlayerRank: Number(leaderboard?.currentPlayer?.playerRank),
      endDate: event.endDate,
    });
  };

  const header = (
    <div className="flex flex-col gap-2.5 py-2.5">
      <div className={cn(styles.headerName, 'relative')}>
        <div className="relative z-10 flex items-center gap-2.5 p-2.5">
          <div
            className={cn(
              styles.headerIcon,
              'bg-back200 flex items-center justify-center overflow-hidden rounded-full'
            )}
          >
            <Image
              src="/assets/images/dashboard/player-leaderboard.svg"
              alt="Player leaderboard"
              width={26}
              height={32}
            />
          </div>
          <div className="text-text300 inline-block flex-1 text-xl font-medium">
            <span className="uppercase">{event?.name}</span>
            {event.pointRules && event.pointRules.length > 0 && (
              <div className="ml-1 inline-block align-middle">
                <InfoTooltip
                  content={
                    <ul className="text-text500 w-full list-inside list-disc text-xs first-letter:capitalize">
                      {event.pointRules.map((rule) => (
                        <li key={rule.id}>{rule.description}</li>
                      ))}
                    </ul>
                  }
                  titleClassName="flex items-center justify-center"
                  contentProps={{
                    collisionPadding: 20,
                    className: 'max-w-[200px]',
                    side: 'bottom',
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="text-green700 px-4 text-sm font-medium md:ml-[70px]">
        {formatDateTime(event?.startDate, 'EEE MMM dd yyyy')} -{' '}
        {formatDateTime(event?.endDate, 'EEE MMM dd yyyy')}
      </div>
      <div className="px-4 pt-5 md:ml-[70px]">
        <Button
          variant="blue"
          className="h-8 text-xs"
          onClick={handleShowRewardsDialog}
        >
          Rewards
        </Button>
      </div>
      <ClientOnly>
        <EventEndedBanner className="my-3" event={event} />
      </ClientOnly>
      <div className="px-4 md:ml-[70px]">
        <PublicUsernameCheckbox
          isHidden={leaderboard?.currentPlayer?.isHidden ?? false}
          eventId={String(event.portalEventId)}
          leaderboardId={String(event.leaderboardId)}
        />
      </div>
    </div>
  );

  if (isLoading) {
    return <SoloEventLeaderboardSkeleton header={header} />;
  }

  if (leaderboard?.rows.length === 0) {
    return <SoloEventLeaderboardEmpty />;
  }

  const currentPlayerRank =
    currentPlayerRow && Number(currentPlayerRow?.playerRank);
  const currentPlayerBracket = leaderboard?.brackets?.find(
    (bracket) =>
      bracket.rewardRankStartValue &&
      bracket.rewardRankEndValue &&
      currentPlayerRank &&
      currentPlayerRank >= bracket.rewardRankStartValue &&
      currentPlayerRank <= bracket.rewardRankEndValue
  );
  const currentPlayerRewards =
    currentPlayerBracket && currentPlayerBracket.rewards;

  return (
    <div>
      {currentPlayerRewards && (
        <YourCurrentRewardsBanner
          endDate={event.endDate}
          currentPlayerRewards={currentPlayerRewards}
        />
      )}
      <div className="bg-back600 rounded-xl">
        {header}
        <div className="text-text400 bg-back300 px-5 pb-4 pt-8 text-xs md:px-[30px] md:pt-5">
          <SoloEventLeaderboardRowLayout
            rank="RANK"
            name="PLAYERS"
            points="POINTS EARNS"
            className="text-text700 pb-4 text-xs font-semibold uppercase"
          />
          {top10.map((player, index) => {
            return (
              <SoloEventLeaderboardRow
                key={index}
                player={player}
                isCurrentPlayer={Boolean(
                  currentPlayerRow && currentPlayerRow.userId === player.userId
                )}
                currentPlayerRow={currentPlayerRow}
                className={cn('even:bg-back400 rounded-xl', {
                  'border-back200 border': index === 0,
                })}
              />
            );
          })}
          {isShowPlayerWithSiblings && (
            <>
              <SoloEventLeaderboardRowLayout
                rank={<span className="text-sm font-semibold">...</span>}
                name={null}
                points={null}
                className="even:bg-back400 flex h-[54px] items-center rounded-xl px-6"
              />
              {isShowAbovePlayer &&
                abovePlayer != null &&
                abovePlayerRank != null && (
                  <SoloEventLeaderboardRow
                    player={abovePlayer}
                    isCurrentPlayer={false}
                  />
                )}
              {currentPlayerRow != null && currentPlayerRowRank != null && (
                <SoloEventLeaderboardRow
                  player={currentPlayerRow}
                  currentPlayerRow={currentPlayerRow}
                  isCurrentPlayer={true}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
